import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";
import { BudgetPlanStyles, formatDate, money, num } from "./BudgetPlanShared";

const List = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);

    try {
      const res = await Http.get(`/budget-plan`);
      setReceiveData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    DatatableFunc();
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const deleteData = (id, deleteApiUrl, showMsg) => {
    Swal.fire({
      title: "Delete this plan?",
      text: "The plan and all of its budget lines will be removed permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2a2962",
      cancelButtonColor: "#e42320",
      confirmButtonText: "Yes, delete it",
    }).then((result) => {
      if (result.isConfirmed) {
        Http.delete("/" + deleteApiUrl + "/" + id)
          .then((res) => {
            setReceiveData(res.data);
            Swal.fire("Deleted!", showMsg + " has been deleted.", "success");
          })
          .catch((e) => {
            Swal.fire(
              "Not deleted",
              e.response?.data?.message || "Please try again.",
              "warning"
            );
          });
      }
    });
  };

  /**
   * Moves a plan through draft -> submitted -> approved|rejected. The server
   * owns the rules; this only asks and then refreshes the list.
   */
  const statusChange = (id, action) => {
    const labels = {
      submit: "Submit this plan for approval?",
      approve: "Approve this plan? It can no longer be edited afterwards.",
      reject: "Reject this plan?",
    };

    Swal.fire({
      title: "Are you sure?",
      text: labels[action],
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#2a2962",
      cancelButtonColor: "#e42320",
      confirmButtonText: "Yes",
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      Http.put("/budget-plan-" + action + "/" + id)
        .then((res) => {
          showAlert(res.data.message, "success");
          fetchData();
        })
        .catch((e) => {
          Swal.fire(
            "Not changed",
            e.response?.data?.message || "Please try again.",
            "warning"
          );
        });
    });
  };

  const summary = useMemo(() => {
    const rows = receiveData || [];
    const byStatus = (status) => rows.filter((row) => row.status === status);
    const sum = (list) => list.reduce((acc, row) => acc + num(row.total), 0);

    return {
      count: rows.length,
      approvedValue: sum(byStatus("approved")),
      submitted: byStatus("submitted").length,
      draft: byStatus("draft").length,
      rejected: byStatus("rejected").length,
      totalValue: sum(rows),
    };
  }, [receiveData]);

  const dateBodyTemplate = (field) => {
    const Component = (rowData) => (
      <span style={{ color: "#35374d" }}>{formatDate(rowData[field])}</span>
    );
    Component.displayName = `DateBodyTemplate(${field})`;
    return Component;
  };

  const periodBodyTemplate = (rowData) => (
    <span style={{ fontWeight: 600, color: "#2a2962" }}>
      {formatDate(rowData.start_date)}
    </span>
  );

  const messageBodyTemplate = (rowData) =>
    rowData.message ? (
      <span title={rowData.message}>{rowData.message}</span>
    ) : (
      <span style={{ color: "#b6bacb" }}>—</span>
    );

  const totalBodyTemplate = (rowData) => (
    <span
      className="bp-num"
      style={{ display: "block", fontWeight: 700, color: "#2a2962" }}
    >
      {money(rowData.total)}
    </span>
  );

  const userBodyTemplate = (field) => {
    const Component = (rowData) =>
      rowData[field]?.name ? (
        <span>{rowData[field].name}</span>
      ) : (
        <span style={{ color: "#b6bacb" }}>—</span>
      );
    Component.displayName = `UserBodyTemplate(${field})`;
    return Component;
  };

  const cols = [
    {
      field: "start_date",
      header: "Start Date",
      body: periodBodyTemplate,
    },
    {
      field: "end_date",
      header: "End Date",
      body: dateBodyTemplate("end_date"),
    },
    { field: "message", header: "Remarks", body: messageBodyTemplate },
    {
      field: "total",
      header: "Total Budget",
      body: totalBodyTemplate,
      headerClassName: "bp-num",
    },
    // The leading space picks the workflow tag in ReactDataTable rather than
    // the active/inactive one.
    { field: "status", header: " Status" },
    {
      field: "created_by.name",
      header: "Created By",
      body: userBodyTemplate("created_by"),
    },
    {
      field: "approved_by.name",
      header: "Approved By",
      body: userBodyTemplate("approved_by"),
    },
  ];

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <BudgetPlanStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">Finance Plans</h5>
            <p className="text-muted mb-0 tx-12">
              Budget allocations per chart of account, from draft through
              approval
            </p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={fetchData}
              title="Reload data"
            >
              <i className="fa fa-refresh"></i>
            </button>
            <Link to="/create-budget-plan" className="btn btn-primary">
              <i className="fa fa-plus-square mg-r-6"></i>New Finance Plan
            </Link>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="row mg-b-20">
            <div className="col-md-3 mg-b-10">
              <div className="bp-stat">
                <i className="fa fa-folder-o bp-stat-icon"></i>
                <span className="bp-stat-label">Total Plans</span>
                <span className="bp-stat-value">{summary.count}</span>
                <span className="bp-stat-sub">
                  {money(summary.totalValue)} budgeted in total
                </span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="bp-stat is-ok">
                <i className="fa fa-check-circle-o bp-stat-icon"></i>
                <span className="bp-stat-label">Approved Value</span>
                <span className="bp-stat-value">
                  {money(summary.approvedValue)}
                </span>
                <span className="bp-stat-sub">Available to spend against</span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="bp-stat is-pending">
                <i className="fa fa-clock-o bp-stat-icon"></i>
                <span className="bp-stat-label">Awaiting Approval</span>
                <span className="bp-stat-value">{summary.submitted}</span>
                <span className="bp-stat-sub">
                  {summary.draft} still in draft
                </span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="bp-stat is-alert">
                <i className="fa fa-times-circle-o bp-stat-icon"></i>
                <span className="bp-stat-label">Rejected</span>
                <span className="bp-stat-value">{summary.rejected}</span>
                <span className="bp-stat-sub">Nothing can be booked</span>
              </div>
            </div>
          </div>

          <div className="bp-panel">
            <div className="bp-panel-head">
              <span>
                <i className="fa fa-table me-2"></i>All Finance Plans
              </span>
            </div>
            <div className="bp-panel-body">
              <ReactDataTable
                cols={cols}
                receiveData={receiveData}
                isLoading={loading}
                actionTemplate={actionTemplate}
                deleteData={deleteData}
                statusChange={statusChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const actionTemplate = (item, deleteData, statusChange) => {
  const approved = item.status === "approved";
  const submitted = item.status === "submitted";

  return (
    <div className="d-flex gap-1 bp-no-print">
      <Link
        to={{ pathname: "/show-budget-plan/" + item.id }}
        className="btn btn-outline-secondary btn-sm"
        title="Details"
      >
        <i className="fa fa-list" aria-hidden="true"></i>
      </Link>
      {!approved && (
        <Link
          to={{ pathname: "/edit-budget-plan/" + item.id }}
          className="btn btn-outline-secondary btn-sm"
          title="Edit"
        >
          <i className="fa fa-pencil" aria-hidden="true"></i>
        </Link>
      )}

      {(item.status === "draft" || item.status === "rejected") && (
        <button
          className="btn btn-outline-primary btn-sm"
          title="Submit for approval"
          onClick={() => statusChange(item.id, "submit")}
        >
          <i className="fa fa-paper-plane"></i>
        </button>
      )}
      {submitted && (
        <>
          <button
            className="btn btn-outline-success btn-sm"
            title="Approve"
            onClick={() => statusChange(item.id, "approve")}
          >
            <i className="fa fa-check"></i>
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            title="Reject"
            onClick={() => statusChange(item.id, "reject")}
          >
            <i className="fa fa-times"></i>
          </button>
        </>
      )}

      {!approved && (
        <button
          className="btn btn-outline-danger btn-sm"
          title="Delete"
          onClick={() => deleteData(item.id, "budget-plan", "Budget Plan")}
        >
          <i className="fa fa-trash"></i>
        </button>
      )}
    </div>
  );
};

export default List;
